/**
 * =============================================================================
 * Componente: AffiliateDesktopSidebar
 * Versão: 2.0.0 (Branding por tenant + Identidade no rodapé)
 *
 * Mudanças v2.0.0:
 *   - Topo: agora exibe a LOGO da empresa (configurada pelo admin do tenant)
 *     com fallback para o ícone "A" + label "Afiliado".
 *   - Rodapé: bloco de identidade do afiliado (avatar gradiente + nome + email),
 *     que antes vivia no topo. Card "Saldo disponível" foi REMOVIDO.
 *   - Não há mais leitura de saldo aqui (nem do useAffiliateHomeOverview).
 *
 * Descrição:
 *   Sidebar fixa do desktop, exclusiva do painel do afiliado. Identidade da
 *   empresa no topo, navegação agrupada e identidade do afiliado + logout
 *   no rodapé.
 * =============================================================================
 */
import { NavLink } from 'react-router-dom';
import {
  Home,
  Megaphone,
  Users,
  Wallet,
  Banknote,
  ImageIcon as ImageIconLucide,
  UserCircle,
  LogOut,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAffiliateAuth } from '@/hooks/useAffiliateAuth';
import { useAffiliateBranding } from '@/hooks/useAffiliateBranding';
import { SmartAvatar } from '@/components/ui/smart-avatar';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}

const GROUPS: { title: string; items: NavItem[] }[] = [
  {
    title: 'Operação',
    items: [
      { to: '/afiliado', label: 'Início', icon: Home, end: true },
      { to: '/afiliado/links', label: 'Campanhas', icon: Megaphone },
      { to: '/afiliado/indicacoes', label: 'Indicações', icon: Users },
      { to: '/afiliado/materiais', label: 'Materiais', icon: ImageIconLucide },
    ],
  },
  {
    title: 'Financeiro',
    items: [
      { to: '/afiliado/comissoes', label: 'Comissões', icon: Wallet },
      { to: '/afiliado/saques', label: 'Saques', icon: Banknote },
    ],
  },
  {
    title: 'Conta',
    items: [{ to: '/afiliado/perfil', label: 'Perfil', icon: UserCircle }],
  },
];

function getInitial(name?: string | null): string {
  if (!name) return 'A';
  return name.trim().charAt(0).toUpperCase() || 'A';
}

export function AffiliateDesktopSidebar() {
  const { affiliate, signOut } = useAffiliateAuth();
  const initial = getInitial(affiliate?.full_name);

  // Branding personalizado do tenant (logo + nome da empresa)
  const { data: brandingData } = useAffiliateBranding(affiliate?.tenant_id ?? null);
  const logoUrl = brandingData?.branding?.logo_url ?? null;
  const tenantName = brandingData?.tenant_name ?? 'Afiliado';

  return (
    <aside
      aria-label="Navegação do painel do afiliado"
      className={cn(
        'hidden md:flex h-screen sticky top-0 w-[260px] flex-col',
        'bg-[hsl(var(--aff-surface))] border-r border-[hsl(var(--aff-border))]',
      )}
    >
      {/* Topo: marca da empresa.
          - Com logo configurada: a logo ocupa toda a largura útil da sidebar,
            sem nome do tenant nem subtítulo, dando máximo destaque à marca.
          - Sem logo: mantém o fallback original (ícone gradiente + nome + label). */}
      <div className="px-4 pt-5 pb-4">
        {logoUrl ? (
          <div className="flex h-16 w-full items-center justify-center">
            <img
              src={logoUrl}
              alt={tenantName}
              className="max-h-16 max-w-full object-contain"
            />
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div
              aria-hidden
              className="flex h-11 w-11 items-center justify-center rounded-2xl text-[hsl(var(--aff-primary-foreground))]"
              style={{ background: 'var(--aff-gradient-hero)' }}
            >
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-semibold text-[hsl(var(--aff-foreground))]">
                {tenantName}
              </p>
              <p className="truncate text-[11px] uppercase tracking-wider text-[hsl(var(--aff-muted-foreground))]">
                Painel de afiliado
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Navegação agrupada */}
      <nav className="flex-1 overflow-y-auto px-3 py-3">
        {GROUPS.map((group) => (
          <div key={group.title} className="mb-4">
            <p className="px-3 pb-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-[hsl(var(--aff-muted-foreground))]">
              {group.title}
            </p>
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 rounded-xl px-3 py-2.5 text-[13.5px] font-medium transition-colors',
                        isActive
                          ? 'bg-[hsl(var(--aff-primary-soft))] text-[hsl(var(--aff-primary))]'
                          : 'text-[hsl(var(--aff-muted-foreground))] hover:bg-[hsl(var(--aff-surface-2))] hover:text-[hsl(var(--aff-foreground))]',
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <item.icon
                          className="h-[17px] w-[17px] shrink-0"
                          strokeWidth={isActive ? 2.4 : 2}
                        />
                        <span className="truncate">{item.label}</span>
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* Rodapé: identidade do afiliado (movida do topo) + sair */}
      <div className="p-3 space-y-2">
        <div className="flex items-center gap-3 rounded-2xl bg-[hsl(var(--aff-surface-2))] p-2.5">
          <SmartAvatar
            src={affiliate?.avatar_url}
            name={affiliate?.full_name}
            email={affiliate?.email}
            size="md"
            fallback={initial}
            fallbackClassName="text-[hsl(var(--aff-primary-foreground))]"
          />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-semibold text-[hsl(var(--aff-foreground))]">
              {affiliate?.full_name ?? 'Afiliado'}
            </p>
            <p className="truncate text-[11px] text-[hsl(var(--aff-muted-foreground))]">
              {affiliate?.email}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => signOut()}
          className="w-full justify-start gap-2 text-[hsl(var(--aff-muted-foreground))] hover:bg-[hsl(var(--aff-surface-2))] hover:text-[hsl(var(--aff-danger))]"
        >
          <LogOut className="h-4 w-4" />
          Sair
        </Button>
      </div>
    </aside>
  );
}
